import { useState, useMemo, useEffect } from "react";
import { Download } from "lucide-react";
import DropDownComponent from "../components/common/DropDownComponent";
import { useGetAllBranches } from "../service/branchService";
import {
  useGetAllCounters,
  useGetCounterSummary,
  usePostCounterClose,
} from "../service/counterService";
import { useGetAllUsers } from "../service/userService";
import { toast } from "react-toastify";
import {
  useGetAllAccountingEntries,
  useDownloadAccountingExcel,
  type AccountingEntry,
} from "../service/accountingService";
import AccountingListTable from "../components/AccountingList";
import { ApprovalLog } from "./AprovalLogComponent";
import { useNavigate } from "react-router-dom";

export default function AccountingList() {
  const navigate = useNavigate();

  const [selectedBranch, setSelectedBranch] = useState(
    localStorage.getItem("branchId") || "",
  );
  const [selectedCounter, setSelectedCounter] = useState(
    localStorage.getItem("counterId") || "",
  );
  const [selectedUser, setSelectedUser] = useState(
    localStorage.getItem("userId") || "",
  );

  // Check if the counter session is open; redirect to landing if not
  const {
    data: counterSummary,
    isError: counterSummaryError,
    isSuccess: counterSummarySuccess,
  } = useGetCounterSummary({
    branchId: selectedBranch || localStorage.getItem("branchId") || undefined,
    counterId:
      selectedCounter || localStorage.getItem("counterId") || undefined,
  });

  useEffect(() => {
    if (counterSummaryError) {
      navigate("/accounting-landing");
    }
  }, [counterSummaryError, navigate]);

  useEffect(() => {
    if (counterSummarySuccess && counterSummary?.status !== "OPEN") {
      navigate("/accounting-landing");
    }
  }, [counterSummarySuccess, counterSummary, navigate]);

  // Approval Log state
  const [confirmBankBalance, setConfirmBankBalance] = useState("");
  const [confirmCash, setConfirmCash] = useState("");
  const [remarks, setRemarks] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: branchData } = useGetAllBranches({
    params: {
      search: "",
      isActive: true,
      page: 0,
      size: 100,
      sortBy: "branchName",
      sortDir: "asc",
    },
  });

  const { data: counterData } = useGetAllCounters({
    params: {
      search: "",
      page: 0,
      size: 100,
      branchId: selectedBranch ? Number(selectedBranch) : 0,
    },
  });

  const { data: usersData } = useGetAllUsers({
    pageSize: 100,
    pageNo: 0,
    userName: "",
  });

  const closeCounterMutation = usePostCounterClose();
  const { mutate: downloadExcel, isPending: isDownloading } = useDownloadAccountingExcel();

  const branchOptions = useMemo(
    () =>
      branchData?.content.map((b) => ({
        id: b.branchId,
        name: b.branchName,
      })) ?? [],
    [branchData],
  );

  const counterOptions = useMemo(
    () =>
      counterData?.content.map((c) => ({ id: c.id, name: c.counterName })) ??
      [],
    [counterData],
  );

  const userOptions = useMemo(() => {
    const users = usersData?.userDetailsResponse;
    if (!users) return [];
    return (Array.isArray(users) ? users : [users]).map((u) => ({
      id: u.id,
      name: u.username,
    }));
  }, [usersData]);

  const { data: AccountingEntriesData, isLoading } = useGetAllAccountingEntries(
    {
      branchId: selectedBranch || undefined,
      counterId: selectedCounter || undefined,
      staffId: selectedUser || undefined,
      date: new Date().toISOString().split("T")[0],
      pageNo: 0,
      pageSize: 100,
      sortBy: "date",
      sortDir: "desc",
    },
  );

  const entries: AccountingEntry[] = AccountingEntriesData?.entries ?? [];

  const totalCredit = entries.reduce((s, r) => s + (r.credit ?? 0), 0);
  const totalDebit = entries.reduce((s, r) => s + (r.debit ?? 0), 0);

  // Approval Log derived values
  const bankClosingAmount = AccountingEntriesData?.expectedBank ?? 0;
  const physicalCashCount = AccountingEntriesData?.expectedCash ?? 0;
  const confirmedBank =
    confirmBankBalance !== "" ? Number(confirmBankBalance) : null;
  const confirmedCash = confirmCash !== "" ? Number(confirmCash) : null;
  const difference =
    confirmedBank !== null && confirmedCash !== null
      ? confirmedBank + confirmedCash - (bankClosingAmount + physicalCashCount)
      : null;

  const handleApprovalSubmit = () => {
    if (!remarks.trim()) {
      toast.error("Please enter remarks before submitting.");
      return;
    }
    setIsSubmitting(true);
    closeCounterMutation.mutate(
      {
        counterId: Number(selectedCounter),
        closingCash: confirmedCash ?? 0,
        closingBank: confirmedBank ?? 0,
        remarks,
        varianceReason: difference !== null ? difference.toString() : "",
        branchId:
          selectedBranch || localStorage.getItem("branchId") || undefined,
      },
      {
        onSuccess: () => {
          toast.success("Approval log submitted successfully!");
          setIsSubmitting(false);
          setConfirmBankBalance("");
          setConfirmCash("");
          setRemarks("");
          navigate("/accounting-landing");
        },
        onError: (error: Error) => {
          toast.error(`Failed to submit approval log: ${error.message}`);
          setIsSubmitting(false);
        },
      },
    );
  };

  const fmt = (val: number | null) =>
    val !== null && val !== undefined ? val.toLocaleString("en-IN", { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : "--";

  const handleExport = () => {
    downloadExcel({
      branchId: selectedBranch || undefined,
      counterId: selectedCounter || undefined,
      staffId: selectedUser || undefined,
      date: new Date().toISOString().split("T")[0],
      sortBy: "date",
      sortDir: "desc",
    }, {
      onSuccess: () => {
        toast.success("Excel downloaded successfully");
      },
      onError: (error: Error) => {
        toast.error(`Export failed: ${error.message}`);
      }
    });
  };

  return (
    <div className="space-y-3">
      {/* Page header */}
      <div className="rounded-[16px] border border-[#E8EEF9] bg-white shadow-[0_4px_16px_rgba(26,94,249,0.04)] px-5 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-[#1A5EF9]">Accounting</h1>
            <p className="text-xs text-[#7D8998]">
              Day-end transaction summary and session closure
            </p>
          </div>
          <button
            onClick={handleExport}
            disabled={isDownloading}
            className="h-9 flex items-center gap-2 px-4 border border-[#1A5EF9] text-[#1A5EF9] rounded-[10px] hover:bg-[#EEF4FF] font-medium transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className={`w-3.5 h-3.5 ${isDownloading ? "animate-bounce" : ""}`} />
            {isDownloading ? "Exporting..." : "Export Excel"}
          </button>
        </div>
      </div>

      {/* Main content: table + approval panel */}
      <div className="flex flex-col lg:flex-row gap-3">
        {/* Table */}
        <AccountingListTable
          isLoading={isLoading}
          entries={entries}
          totalCredit={totalCredit}
          totalDebit={totalDebit}
          receivable={AccountingEntriesData?.receivable ?? 0}
          payable={AccountingEntriesData?.payable ?? 0}
          fmt={fmt}
        />

        {/* Approval Log panel */}
        <div className="w-full lg:w-[320px] shrink-0">
          <ApprovalLog
            bankClosingAmount={bankClosingAmount}
            physicalCashCount={physicalCashCount}
            confirmBankBalance={confirmBankBalance}
            confirmCash={confirmCash}
            remarks={remarks}
            difference={difference}
            isSubmitting={isSubmitting}
            onConfirmBankBalanceChange={setConfirmBankBalance}
            onConfirmCashChange={setConfirmCash}
            onRemarksChange={setRemarks}
            onSubmit={handleApprovalSubmit}
          />
        </div>
      </div>
    </div>
  );
}
